<template>
  <v-dialog v-model="dialog" max-width="900" scrollable>
    <v-card :loading="loadingProfile" class="rounded-xl">
      <v-card-title class="text-body-2 gap-2">
        <base-avatar
          :src="user?.photo"
          :alt="user?.name"
          :size="40"
          :seed="user?.id || ''"
        />
        <div>
          <p class="mb-0 font-weight-bold lh-1">
            {{ user?.name || "Conta não criada" }}
          </p>
          <small>
            {{ user?.phone }}
            • {{ user?.email || "Não informado" }}
          </small>
        </div>
        <v-spacer />
        <v-chip
          small
          label
          :color="status.color"
          text-color="white"
          class="align-self-start"
        >
          {{ status.text }}
        </v-chip>
      </v-card-title>

      <v-tabs v-model="tab" grow show-arrows height="40">
        <v-tab>
          <v-icon left small>mdi-account-details</v-icon>
          Básico
        </v-tab>
        <v-tab>
          <v-icon left small>mdi-cart</v-icon>
          Compras
        </v-tab>
        <v-tab>
          <v-icon left small>mdi-shield-account</v-icon>
          Segurança
        </v-tab>
      </v-tabs>
      <v-divider />

      <v-card-text class="pt-4">
        <v-tabs-items v-model="tab" touchless>
          <v-tab-item>
            <v-alert v-if="error" type="error" class="mb-4">{{ error }}</v-alert>
            <user-basic :user="user" :loading="loadingProfile" />
          </v-tab-item>
          <v-tab-item>
            <v-alert v-if="paymentsError" type="error" class="mb-4">
              {{ paymentsError }}
            </v-alert>
            <v-skeleton-loader
              v-if="loadingPayments && !payments"
              type="article, article"
            />
            <user-payments v-else :user="paymentsUser" :loading="loadingPayments" />
          </v-tab-item>
          <v-tab-item>
            <v-alert v-if="securityError" type="error" class="mb-4">
              {{ securityError }}
            </v-alert>
            <v-skeleton-loader
              v-if="loadingSecurity && !logins"
              type="table-heading, table-tbody"
            />
            <user-security v-else :user="securityUser" :loading="loadingSecurity" />
          </v-tab-item>
        </v-tabs-items>
      </v-card-text>

      <v-card-actions>
        <v-menu offset-y top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn text v-bind="attrs" v-on="on" :disabled="loadingProfile || !user?.id">
              Operações
              <v-icon right small>mdi-chevron-down</v-icon>
            </v-btn>
          </template>
          <v-list dense>
            <v-list-item @click="openEdit">
              <v-list-item-icon class="mr-2">
                <v-icon small>mdi-pencil</v-icon>
              </v-list-item-icon>
              <v-list-item-title>Alterar dados</v-list-item-title>
            </v-list-item>
            <v-list-item @click="openActive">
              <v-list-item-icon class="mr-2">
                <v-icon small :color="user?.active ? 'error' : 'success'">
                  {{ user?.active ? "mdi-account-cancel" : "mdi-account-check" }}
                </v-icon>
              </v-list-item-icon>
              <v-list-item-title>
                {{ user?.active ? "Bloquear" : "Desbloquear" }}
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        <v-spacer />
        <v-btn text @click="close">Fechar</v-btn>
      </v-card-actions>
      <user-edit-modal ref="editModal" @saved="onUserSaved" />
      <user-active-modal ref="activeModal" @saved="onUserSaved" />
    </v-card>
  </v-dialog>
</template>

<script>
import USERS from "@/services/ultimate/users";
import UserBasic from "./components/UserBasic.vue";
import UserPayments from "./components/UserPayments.vue";
import UserSecurity from "./components/UserSecurity.vue";
import UserEditModal from "./UserEditModal.vue";
import UserActiveModal from "./UserActiveModal.vue";

export default {
  name: "UserDetailsModal",
  components: {
    UserBasic,
    UserPayments,
    UserSecurity,
    UserEditModal,
    UserActiveModal,
  },
  data() {
    return {
      dialog: false,
      tab: 0,
      user: null,
      payments: null,
      logins: null,
      tokens: null,
      loadingProfile: false,
      loadingPayments: false,
      loadingSecurity: false,
      error: null,
      paymentsError: null,
      securityError: null,
      paymentsUserId: null,
      securityUserId: null,
      paymentsRequestId: null,
      securityRequestId: null,
    };
  },
  watch: {
    tab(value) {
      if (value === 1) this.loadPayments();
      if (value === 2) this.loadSecurity();
    },
  },
  methods: {
    open(user) {
      this.user = user ? { ...user } : null;
      this.payments = null;
      this.logins = null;
      this.tokens = null;
      this.tab = 0;
      this.dialog = true;
      this.error = null;
      this.paymentsError = null;
      this.securityError = null;
      this.paymentsUserId = null;
      this.securityUserId = null;
      this.paymentsRequestId = null;
      this.securityRequestId = null;
      this.loadingPayments = false;
      this.loadingSecurity = false;
      this.loadProfile();
    },
    async loadProfile() {
      const userId = this.user?.id;
      if (!userId) return;

      this.loadingProfile = true;
      this.error = null;
      try {
        const response = await USERS.getUser(userId);
        if (this.user?.id !== userId) return;
        this.user = response.user;
      } catch (error) {
        if (this.user?.id !== userId) return;
        this.error = error.message || "Erro ao buscar usuário";
      } finally {
        if (this.user?.id === userId) this.loadingProfile = false;
      }
    },
    async loadPayments() {
      const userId = this.user?.id;
      if (!userId || this.paymentsUserId === userId) return;
      if (this.paymentsRequestId === userId) return;

      this.paymentsRequestId = userId;
      this.loadingPayments = true;
      this.paymentsError = null;
      try {
        const response = await USERS.getUserPayments(userId);
        if (this.user?.id !== userId) return;
        this.payments = response.payments || [];
        this.paymentsUserId = userId;
      } catch (error) {
        if (this.user?.id !== userId) return;
        this.paymentsError = error.message || "Erro ao buscar compras";
      } finally {
        if (this.paymentsRequestId === userId) {
          this.loadingPayments = false;
          this.paymentsRequestId = null;
        }
      }
    },
    async loadSecurity() {
      const userId = this.user?.id;
      if (!userId || this.securityUserId === userId) return;
      if (this.securityRequestId === userId) return;

      this.securityRequestId = userId;
      this.loadingSecurity = true;
      this.securityError = null;
      try {
        const response = await USERS.getUserSecurity(userId);
        if (this.user?.id !== userId) return;
        this.logins = response.logins || [];
        this.tokens = response.tokens || [];
        this.securityUserId = userId;
      } catch (error) {
        if (this.user?.id !== userId) return;
        this.securityError = error.message || "Erro ao buscar segurança";
      } finally {
        if (this.securityRequestId === userId) {
          this.loadingSecurity = false;
          this.securityRequestId = null;
        }
      }
    },
    close() {
      this.dialog = false;
    },
    openEdit() {
      if (!this.user?.id) return;
      this.$refs.editModal.open(this.user);
    },
    openActive() {
      if (!this.user?.id) return;
      this.$refs.activeModal.open(this.user);
    },
    async onUserSaved() {
      await this.loadProfile();
      this.$root.$emit("user-updated", this.user);
    },
  },
  computed: {
    paymentsUser() {
      return { ...this.user, Payments: this.payments };
    },
    securityUser() {
      return {
        ...this.user,
        LoginAttempt: this.logins,
        UserToken: this.tokens,
      };
    },
    status() {
      if (!this.user?.active) return { color: "error", text: "Inativo" };
      if (!this.user?.complete) return { color: "warning", text: "Incompleto" };
      return { color: "success", text: "Ativo" };
    },
  },
  mounted() {
    this.$root.$on("user-details-modal", this.open);
  },
  beforeDestroy() {
    this.$root.$off("user-details-modal", this.open);
  },
};
</script>
